/**
 * Vault-equivalent values for every leaf in `atlasSemantic.color`.
 *
 * Typed via `DeepStringValues<AtlasSemantic['color']>` which enforces the exact key
 * structure (any missing/extra key is a compile error) but widens leaf literal string
 * types to `string` — necessary because `atlasSemantic` uses `as const` so its leaf
 * types are literal hex strings, not `string`.
 *
 * Mapping rationale is documented in `.cursor/atlas-ff-token-mapping.md`.
 * Values sourced from `main` branch `newPalette` via `vaultPaletteSource.ts`.
 */

import type { AtlasSemantic } from '../tokens';

/** Recursively widens all leaf string literals to `string` while preserving key shape. */
type DeepStringValues<T> = T extends string
  ? string
  : { [K in keyof T]: DeepStringValues<T[K]> };
import {
  vaultChart,
  vaultGreen,
  vaultNeutral,
  vaultNavColors,
  vaultRed,
  vaultTurquoise,
  vaultYellow,
} from './vaultPaletteSource';

// Atlas dataVisualization palettes with no Vault equivalent — pass through unchanged.
// Imported from tokens to keep them bit-for-bit identical (not re-specified here).
import { atlasSemantic } from '../tokens';

const { dataVisualization } = atlasSemantic.color;

export const vaultColorOverrides: DeepStringValues<AtlasSemantic['color']> = {
  action: {
    primary: {
      default: vaultTurquoise.T300, // #35A191 — was Atlas gray #282e37
      hover: vaultTurquoise.T400, // #168070
      active: vaultTurquoise.T500, // #096C5E
      disabled: vaultNeutral.N200, // #F2F2F3
      onPrimary: vaultNeutral.N0, // #FFFFFF (same)
      onPrimaryDisabled: vaultNeutral.N500, // #A2A6A8
    },
    secondary: {
      outline: vaultTurquoise.T300, // #35A191 — Vault secondary outline was teal
      hoverFill: vaultTurquoise.T100, // #D8EFEB
      activeFill: vaultNeutral.N200, // #F2F2F3
      disabledOutline: vaultNeutral.N200, // #F2F2F3
      onSecondary: vaultNeutral.N800, // #17353D — Vault secondary button text was dark (N800)
    },
    destructive: {
      default: vaultRed.R300, // #E2342A
      hover: vaultRed.R400, // #BE2714
      active: vaultRed.R500, // #8F211B
      disabled: `${vaultRed.R300}80`, // R300 + 50% opacity
      onDestructive: vaultNeutral.N0, // #FFFFFF (same)
    },
    link: {
      default: vaultTurquoise.T300, // #35A191 — Vault links were teal
      hover: vaultTurquoise.T400, // #168070
      active: vaultTurquoise.T500, // #096C5E
      disabled: vaultNeutral.N400, // #C6C8C9
    },
    secondaryInverse: {
      hover: vaultNeutral.N900, // #12292F — Vault manager nav sub-item hover/active (main branch NavLink N900)
      active: vaultNeutral.N900, // #12292F — same as hover for focus state
      selected: vaultNavColors.navActiveBg, // #1F4751 — Vault manager nav active/open section
      onSelected: vaultNeutral.N0, // #FFFFFF (same)
      onSecondaryInverse: vaultNeutral.N0, // #FFFFFF (same)
      onSecondaryInverseDisabled: '#6f7377', // unchanged
      highlighted: '#8c8e92', // unchanged
      selectionIndicator: vaultTurquoise.T300, // #35A191 — Vault used teal indicator
      outlineDisabled: '#464e53', // unchanged
      selectionIndicatorTabs: vaultTurquoise.T300, // #35A191 — Vault tab indicator was teal
    },
  },
  type: {
    default: vaultNeutral.N800, // #17353D
    muted: vaultNeutral.N600, // #71717A
    disabled: vaultNeutral.N500, // #A2A6A8
    inverse: vaultNeutral.N0, // #FFFFFF (same)
  },
  background: {
    base: vaultNeutral.N0, // #FFFFFF (same)
    container: vaultNeutral.N200, // #F2F2F3
    navSelection: vaultTurquoise.T300, // #35A191 — Vault nav selection was teal
    backdrop: `${vaultNeutral.N800}80`, // N800 + 50% opacity
  },
  surface: {
    default: vaultNeutral.N0, // #FFFFFF (same)
    variant: vaultNeutral.N200, // #F2F2F3
    subtle: vaultNeutral.N100, // #FAFBFF
    inverse: vaultNeutral.N800, // #17353D — Vault dark surface was teal-dark
  },
  outline: {
    fixed: vaultNeutral.N300, // #E4E5E6
    default: vaultNeutral.N400, // #C6C8C9
    hover: vaultNeutral.N500, // #A2A6A8
    active: vaultNeutral.N1000, // #0A181B
    disabled: vaultNeutral.N200, // #F2F2F3
  },
  ui: {
    focusRing: '#0b4cce', // unchanged — accessibility
    focusInverse: vaultNeutral.N0, // #FFFFFF (same)
    loading: {
      default: vaultTurquoise.T200, // #81BBB1 — Vault had teal loading
      variant: vaultNeutral.N600, // #71717A
    },
    divider: {
      default: vaultNeutral.N300, // #E4E5E6
      secondary: vaultNeutral.N500, // #A2A6A8
    },
    scrollbar: {
      handle: vaultNeutral.N400, // #C6C8C9
    },
  },
  status: {
    success: {
      default: vaultTurquoise.T300, // #35A191 — Vault 'investigating' badge was T300 teal
      background: vaultTurquoise.T100, // #D8EFEB — Vault success toast/panel bg was light teal
      text: vaultTurquoise.T400, // #168070 — Vault success icon/accent was T400 teal
      backgroundInverse: vaultGreen.G500, // #016533
      textInverse: vaultGreen.G100, // #BBDBC9
      textDefault: vaultNeutral.N0, // #FFFFFF — white text on T300
    },
    warning: {
      default: vaultYellow.Y400, // #FFB020
      background: vaultYellow.Y100, // #FFF8F2
      text: vaultYellow.Y500, // #A16F14
    },
    error: {
      default: vaultRed.R300, // #E2342A
      background: vaultRed.R100, // #FCF2F2
      text: vaultRed.R500, // #8F211B
      backgroundInverse: vaultRed.R500, // #8F211B
      textInverse: vaultRed.R100, // #FCF2F2
    },
    notification: {
      default: vaultTurquoise.T100, // #D8EFEB — Vault 'draft' badge bg was T100 light teal
      background: '#d7f6ff', // no Vault equiv, keep
      text: vaultNeutral.N600, // #71717A — muted (Vault had no notif text)
      textDefault: vaultNeutral.N800, // #17353D — draft badge text color
    },
    new: {
      default: vaultRed.R300, // #E2342A — red badge
      textDefault: vaultNeutral.N0, // #FFFFFF (same)
    },
    neutral: {
      background: vaultNeutral.N200, // #F2F2F3
      textDefault: vaultNeutral.N800, // #17353D
      backgroundVariant: vaultNeutral.N700, // #546276 — Vault 'read' badge bg was N700
      text: vaultNeutral.N0, // #FFFFFF — white text on N700 bg
    },
  },
  selection: {
    primary: {
      default: vaultNeutral.N800, // #17353D — Vault dark selection bg
      hover: vaultTurquoise.T400, // #168070
      active: vaultNeutral.N900, // #12292F
      onSelected: vaultNeutral.N0, // #FFFFFF — white text on dark bg
    },
    secondary: {
      default: vaultTurquoise.T100, // #D8EFEB — Vault selection was teal tint
      hover: vaultTurquoise.T200, // #81BBB1
      active: vaultNeutral.N100, // #FAFBFF
    },
  },
  accent: {
    red: {
      background: vaultRed.R100, // #FCF2F2
      content: vaultRed.R300, // #E2342A
    },
    yellow: {
      background: vaultYellow.Y100, // #FFF8F2
      content: vaultYellow.Y400, // #FFB020
    },
    green: {
      background: vaultGreen.G100, // #BBDBC9
      content: vaultGreen.G300, // #3EB572
    },
    blue: {
      background: vaultTurquoise.T100, // #D8EFEB — no blue in Vault, use teal
      content: vaultTurquoise.T300, // #35A191
    },
    purple: {
      background: vaultNeutral.N200, // #F2F2F3 — no purple in Vault, use neutral
      content: vaultNeutral.N500, // #A2A6A8
    },
  },
  dataVisualization: {
    qualitative: dataVisualization.qualitative, // no Vault equivalent, keep Atlas
    sequential: dataVisualization.sequential, // no Vault equivalent, keep Atlas
    rag: dataVisualization.rag, // no Vault equivalent, keep Atlas
    chart: {
      blue: vaultChart.blue,
      yellow: vaultChart.yellow,
      red: vaultChart.red,
      green: vaultChart.green,
      pink: vaultChart.pink,
      teal: vaultChart.teal,
      violet: vaultChart.violet,
      yellow2: vaultChart.yellow2,
    },
  },
};
